import AppSettings from '../../../AppSettings';
// Librairies
import i18n from '../../../locales/i18n';
// Utils
import { getLocalObservationIds, rememberObservationIds, resolveElementRefs } from './ElementRefs';
import { mergeFeature } from '../MergeUtil';

const BACKEND_ENDPOINT = AppSettings.getBackendUrl();
// Segment {connectionId} fixe : un rejeu doit garder la même route (clé d'idempotence) ; l'auteur est reconnu par l'en-tête X-Connection-Id
const QUEUE_CONNECTION_SEGMENT = 'outbox';

// Endpoints de l'API et forme de ses réponses, par catégorie : création POST {path}/outbox, modification PUT {path}/?…,
// suppression DELETE {path}/outbox, relecture POST {path}/projects/{id} (réponse { [listKey]: [...] }) ;
// createResultKey et updateResultKey : clé de la feature dans la réponse, null quand la réponse est la feature
export const ELEMENT_CATEGORIES = {
    'Arbre': {
        path: 'trees', listKey: 'trees',
        createResultKey: null, updateResultKey: 'tree',
        historyParam: true, replantingParam: true,
        labels: { create: "Ajout d'un arbre", update: "Modification d'un arbre", delete: "Suppression d'arbres" }
    },
    'Espace vert': {
        path: 'greenSpaces', listKey: 'greenSpaces',
        createResultKey: 'greenSpace', updateResultKey: 'greenSpace',
        historyParam: true, replantingParam: false,
        labels: { create: "Ajout d'un espace vert", update: "Modification d'un espace vert", delete: "Suppression d'espaces verts" }
    },
    'Mobilier': {
        path: 'furnitures', listKey: 'furnitures',
        createResultKey: null, updateResultKey: 'furniture',
        historyParam: true, replantingParam: false,
        labels: { create: "Ajout d'un mobilier", update: "Modification d'un mobilier", delete: "Suppression de mobiliers" }
    },
    'Repère': {
        path: 'markers', listKey: 'markers',
        createResultKey: null, updateResultKey: null,
        historyParam: false, replantingParam: false,
        labels: { create: "Ajout d'un repère", update: "Modification d'un repère", delete: "Suppression de repères" }
    }
};

export const categoryConfig = (op) => ELEMENT_CATEGORIES[op.target.category];

// Paramètres d'URL d'une modification : history et replanting n'existent que pour certaines catégories
const updateUrl = (op, config, context) => {
    const { history = false, replanting = false } = op.payload.flags || {};
    return `${BACKEND_ENDPOINT}${config.path}/?connectionId=${context.getConnectionId()}&id=${op.target.id}`
        + (config.historyParam ? `&history=${history}` : '')
        + (config.replantingParam ? `&replanting=${replanting}` : '');
};

// Libellé du journal de l'écran Synchronisation
const describeElement = (op, kind) => {
    const label = i18n.t(categoryConfig(op)?.labels?.[kind] || "Modification d'un élément");
    const reference = op.payload?.feature?.properties?.projectReference;
    return reference ? `${label} (${reference})` : label;
};

// La réponse devient la version serveur ; la version locale reste affichée si une autre opération attend
const acknowledgeFeature = async (op, serverFeature, context) => {
    const clearLocal = !(await context.outbox.hasPendingFor(op.targetKey, op.id));
    await context.snapshot.acknowledge(op.projectId, op.target.category, serverFeature, { clearLocal });
};

export const elementHandlers = {
    'element.create': {
        describe: op => describeElement(op, 'create'),
        buildRequest: async (op, context) => ({
            request: {
                method: 'post',
                url: `${BACKEND_ENDPOINT}${categoryConfig(op).path}/${QUEUE_CONNECTION_SEGMENT}`,
                data: await resolveElementRefs(op.payload.feature, context)
            },
            localObservationIds: getLocalObservationIds(op.payload.feature)
        }),
        onSuccess: async (op, response, context, prepared) => {
            const { createResultKey } = categoryConfig(op);
            const serverFeature = createResultKey ? response.data?.[createResultKey] : response.data;
            await rememberObservationIds(prepared?.localObservationIds, serverFeature, context);
            await acknowledgeFeature(op, serverFeature, context);
            // Espace vert : photos, fichiers et occurrences repris de ses origines
            const { addedFileInfos, addedOccurrences, updatedSeries } = createResultKey ? response.data : {};
            return { feature: serverFeature, addedFileInfos, addedOccurrences, updatedSeries };
        },
        onAbandon: (op, context) => context.snapshot.revertLocal(op.projectId, op.target.id)
    },
    'element.update': {
        describe: op => describeElement(op, 'update'),
        // Relecture sur la base principale (le PUT remplace tout l'élément) : seuls les champs du terrain y sont appliqués
        buildRequest: async (op, context) => {
            const config = categoryConfig(op);
            const current = await context.request({
                method: 'post',
                url: `${BACKEND_ENDPOINT}${config.path}/projects/${op.projectId}`,
                data: { elementIds: [op.target.id], loadedCustomFields: [], fromPrimary: true }
            });
            const serverFeature = current.data?.[config.listKey]?.find(feature => feature.id === op.target.id);
            // La route lit aussi les éléments supprimés : supprimé ou absent, la suppression gagne
            if (!serverFeature || serverFeature.deletionDate) return { discard: true, reason: 'deleted_on_server' };
            const { merged } = mergeFeature(serverFeature, op.payload.changes);
            return {
                request: {
                    method: 'put',
                    url: updateUrl(op, config, context),
                    data: await resolveElementRefs(merged, context)
                },
                localObservationIds: getLocalObservationIds(merged)
            };
        },
        onSuccess: async (op, response, context, prepared) => {
            const { updateResultKey } = categoryConfig(op);
            const serverFeature = updateResultKey ? response.data?.[updateResultKey] : response.data;
            await rememberObservationIds(prepared?.localObservationIds, serverFeature, context);
            await acknowledgeFeature(op, serverFeature, context);
            return { feature: serverFeature, history: response.data?.history || null };
        },
        onDiscard: (op, context) => context.snapshot.removeElements(op.projectId, op.target.category, [op.target.id]),
        onAbandon: (op, context) => context.snapshot.revertLocal(op.projectId, op.target.id)
    },
    'element.bulkCreate': {
        describe: op => i18n.t("Ajout de {{count}} éléments", { count: op.payload.features.length }),
        buildRequest: async (op, context) => {
            const features = [];
            for (const feature of op.payload.features) features.push(await resolveElementRefs(feature, context));
            return { request: { method: 'post', url: `${BACKEND_ENDPOINT}${categoryConfig(op).path}/bulk/${QUEUE_CONNECTION_SEGMENT}`, data: features, timeout: 60000 + features.length * 500 } };
        },
        // Réponse : un tableau, ou { greenSpaces, addedFileInfos, addedOccurrences, updatedSeries } pour les espaces verts
        onSuccess: async (op, response, context) => {
            const isList = Array.isArray(response.data);
            const features = isList ? response.data : (response.data?.[categoryConfig(op).listKey] || []);
            for (const feature of features) {
                const clearLocal = !(await context.outbox.hasPendingFor(`element:${feature.id}`, op.id));
                await context.snapshot.acknowledge(op.projectId, op.target.category, feature, { clearLocal });
            }
            const { addedFileInfos, addedOccurrences, updatedSeries } = isList ? {} : response.data;
            return { features, addedFileInfos, addedOccurrences, updatedSeries };
        },
        onAbandon: async (op, context) => {
            for (const feature of op.payload.features) await context.snapshot.revertLocal(op.projectId, feature.id);
        }
    },
    'element.bulkUpdate': {
        describe: op => i18n.t("Modification de {{count}} éléments", { count: op.payload.items.length }),
        // Une seule relecture sur la base principale ; supprimé ou absent (supprimé, hors de la zone du rôle), l'élément est écarté
        buildRequest: async (op, context) => {
            const config = categoryConfig(op);
            const ids = op.payload.items.map(item => item.id);
            const current = await context.request({
                method: 'post', url: `${BACKEND_ENDPOINT}${config.path}/projects/${op.projectId}`,
                data: { elementIds: ids, loadedCustomFields: [], fromPrimary: true }, timeout: 60000 + ids.length * 200
            });
            const serverFeatures = new Map((current.data?.[config.listKey] || []).map(feature => [feature.id, feature]));
            const features = [], discardedIds = [];
            for (const item of op.payload.items) {
                const serverFeature = serverFeatures.get(item.id);
                if (!serverFeature || serverFeature.deletionDate) discardedIds.push(item.id);
                else features.push(await resolveElementRefs(mergeFeature(serverFeature, item.changes).merged, context));
            }
            if (!features.length) return { discard: true, reason: 'deleted_on_server' };
            return { request: { method: 'put', url: `${BACKEND_ENDPOINT}${config.path}/bulk/${QUEUE_CONNECTION_SEGMENT}`, data: features, timeout: 60000 + features.length * 500 }, discardedIds };
        },
        onSuccess: async (op, response, context, prepared) => {
            const features = Array.isArray(response.data) ? response.data : [];
            for (const feature of features) {
                const clearLocal = !(await context.outbox.hasPendingFor(`element:${feature.id}`, op.id));
                await context.snapshot.acknowledge(op.projectId, op.target.category, feature, { clearLocal });
            }
            if (prepared?.discardedIds?.length) {
                await context.snapshot.removeElements(op.projectId, op.target.category, prepared.discardedIds);
                await context.syncLog?.add({
                    kind: 'discarded', projectId: op.projectId, opType: op.type, targetKey: op.targetKey, reason: 'deleted_on_server', ids: prepared.discardedIds,
                    label: i18n.t("Modification de {{count}} éléments", { count: prepared.discardedIds.length })
                });
            }
            return { features, discardedIds: prepared?.discardedIds || [] };
        },
        onDiscard: (op, context) => context.snapshot.removeElements(op.projectId, op.target.category, op.payload.items.map(item => item.id)),
        onAbandon: async (op, context) => {
            for (const item of op.payload.items) await context.snapshot.revertLocal(op.projectId, item.id);
        }
    },
    'element.zIndex': {
        describe: () => i18n.t("Changement de plan d'un espace vert"),
        buildRequest: async (op) => ({ request: { method: 'put', url: `${BACKEND_ENDPOINT}greenSpaces/zIndex/${op.target.id}/${op.payload.isFirstPlan}` } }),
        onSuccess: async () => ({})
    },
    'element.delete': {
        describe: op => `${i18n.t(categoryConfig(op)?.labels?.delete || "Suppression d'éléments")} (${op.payload.features.length})`,
        // Les éléments déjà supprimés ailleurs sont retirés de l'envoi : un rejeu recréerait l'historique « Suppression »
        buildRequest: async (op, context) => {
            const config = categoryConfig(op);
            const current = await context.request({
                method: 'post',
                url: `${BACKEND_ENDPOINT}${config.path}/projects/${op.projectId}`,
                data: { elementIds: op.payload.features.map(feature => feature.id), loadedCustomFields: [], fromPrimary: true }
            });
            const alive = new Set((current.data?.[config.listKey] || []).filter(feature => !feature.deletionDate).map(feature => feature.id));
            const features = op.payload.features.filter(feature => alive.has(feature.id));
            if (!features.length) return { discard: true, reason: 'already_deleted' };
            return { request: { method: 'delete', url: `${BACKEND_ENDPOINT}${config.path}/${QUEUE_CONNECTION_SEGMENT}`, data: features } };
        },
        onSuccess: async (op, response, context) => {
            const ids = op.payload.features.map(feature => feature.id);
            await context.snapshot.removeElements(op.projectId, op.target.category, ids);
            return { ids };
        },
        onDiscard: (op, context) => context.snapshot.removeElements(op.projectId, op.target.category, op.payload.features.map(feature => feature.id)),
        onAbandon: async (op, context) => {
            for (const feature of op.payload.features) await context.snapshot.revertLocal(op.projectId, feature.id);
        }
    }
};
